/**
 * Exclure une entree de la Sante du coffre (« Exclude from monitoring » de
 * Proton Pass, « Ignore in Watchtower » de 1Password, « Exclude from Password
 * Health » de Dashlane) : un compte de test, un mot de passe impose par un
 * tiers, un ancien wifi qu'on garde sciemment -- l'utilisateur a juge le
 * risque, l'alerte n'est plus que du bruit qui noie les vraies.
 *
 * L'entree exclue sort des alertes, des compteurs et du score, mais PAS de
 * l'analyse des autres : son mot de passe existe toujours, une autre entree
 * qui le reutilise reste signalee. Et elle n'est jamais cachee : la Sante du
 * coffre la liste a part, avec de quoi la reinclure.
 *
 * Le drapeau vit DANS les secrets chiffres (`secrets.healthExcluded === true`),
 * comme `archived` et `reprompt` : le serveur ne sait pas quelles entrees sont
 * surveillees. Module pur, zero E/S.
 */
import type { EntrySecrets } from '@klavox/crypto-core';

/** L'entree est-elle exclue de la Sante du coffre ? Seul `true` exclut (valeur importee mal formee = surveillee). */
export function isHealthExcluded(secrets: EntrySecrets): boolean {
  return secrets['healthExcluded'] === true;
}

/** Secrets avec (ou sans) l'exclusion ; rien d'autre ne change. Seul `true` est ecrit, l'absence vaut « surveillee ». */
export function withHealthExcluded(secrets: EntrySecrets, excluded: boolean): EntrySecrets {
  const { healthExcluded: _excluded, ...rest } = secrets;
  return excluded ? { ...rest, healthExcluded: true } : rest;
}

/**
 * Ignorer UNE alerte d'une entree (« Ignore » d'une alerte dans Watchtower de 1Password) : le mot de passe faible
 * qu'impose une borne d'entreprise, le wifi qu'on partage expres, l'intranet en `http://` qu'on ne peut pas changer.
 * L'entree reste surveillee pour tout le reste : une fuite ou un sosie la signalent toujours.
 *
 * Ne s'ignorent QUE les alertes que l'utilisateur peut juger mieux que l'outil. Jamais : une fuite du mot de passe,
 * une fuite du site depuis le dernier changement, une adresse sosie -- ce sont des faits d'attaque, pas une politique ;
 * les faire taire reste possible, mais seulement en excluant l'entree entiere, geste delibere et liste a part. Les
 * passkeys disponibles ont deja leur geste (`passkey-elsewhere.ts`), les echeances le leur (retirer la date).
 *
 * Le drapeau vit dans les secrets chiffres (`secrets.healthIgnored`, liste), comme `healthExcluded`.
 */
export const IGNORABLE_HEALTH_ALERTS = ['weak', 'reused', 'old', 'insecureUri', 'missingTotp'] as const;
export type IgnorableHealthAlert = (typeof IGNORABLE_HEALTH_ALERTS)[number];

/** Libelles des badges de la Sante du coffre, repris tels quels. */
export const HEALTH_ALERT_LABELS: Readonly<Record<IgnorableHealthAlert, string>> = {
  weak: 'Faible',
  reused: 'Reutilise',
  old: 'Ancien',
  insecureUri: 'Non chiffre',
  missingTotp: '2FA disponible',
};

export function isIgnorableHealthAlert(value: unknown): value is IgnorableHealthAlert {
  return typeof value === 'string' && (IGNORABLE_HEALTH_ALERTS as readonly string[]).includes(value);
}

/**
 * Alertes ignorees de l'entree, dans l'ordre canonique, sans doublon. Une valeur inconnue (une alerte jamais
 * ignorable comme `breached`, une saisie importee mal formee) est lue comme absente : elle ne fait rien taire.
 */
export function readIgnoredHealthAlerts(secrets: EntrySecrets): readonly IgnorableHealthAlert[] {
  const raw = secrets['healthIgnored'];
  if (!Array.isArray(raw)) return [];
  return IGNORABLE_HEALTH_ALERTS.filter((alert) => raw.includes(alert));
}

/** Secrets avec (ou sans) l'alerte ignoree ; rien d'autre ne change. Liste vide = cle retiree. */
export function withIgnoredHealthAlert(secrets: EntrySecrets, alert: IgnorableHealthAlert, ignored: boolean): EntrySecrets {
  const current = readIgnoredHealthAlerts(secrets);
  const next = IGNORABLE_HEALTH_ALERTS.filter((candidate) => (candidate === alert ? ignored : current.includes(candidate)));
  const { healthIgnored: _ignored, ...rest } = secrets;
  return next.length > 0 ? { ...rest, healthIgnored: [...next] } : rest;
}
